<!-- User Architecture sections — projected into the shared shell in docs2.component.html.
     Wrapped so sections get the same 16-unit vertical rhythm as the shell's base
     sections (the shell's space-y only reaches its own direct children). -->
<div class="flex flex-col gap-16">

<!-- =========================== OVERVIEW ========================== -->
<section id="arch-overview" class="scroll-mt-16">
  <div class="border-t border-gray-200 pt-10 mb-8"></div>
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">User Architecture</p>
  <h1 class="text-5xl font-bold mb-6 text-black tracking-tight">How it's built</h1>
  <p class="text-lg text-black leading-relaxed font-light mb-8">
    Clinical AI Studio is one platform with pluggable capability modules, built on a layered architecture
    with clean seams between the interface, orchestration, the reasoning engine, its tools, and data. This
    section walks through the platform's shape, how it deploys and stays secure, and how the engine and its
    tools fit together.
  </p>
  <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4">
    <p class="text-sm text-black leading-relaxed">
      The reasoning engine's internals &mdash; orchestration, model routing, and prompt &amp; agent design
      &mdash; are proprietary. They are described here by their inputs, outputs, and behaviour rather than
      their internal implementation.
    </p>
  </div>
</section>

<!-- =========================== PLATFORM ========================== -->
<section id="arch-platform" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">The suite</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">One platform, pluggable modules</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    Clinical AI Studio is the product you log into. Everything else is a capability module that plugs into the
    same platform &mdash; they share one authentication, audit, and compliance backbone, so capability can be
    added without adding integrations.
  </p>

  <div class="rounded-2xl bg-[#1594fd] p-6 text-white mb-6">
    <span class="text-xs font-semibold uppercase tracking-wider text-white/80">The platform</span>
    <h3 class="text-2xl font-bold mt-1 mb-2">Clinical AI Studio</h3>
    <p class="text-sm leading-relaxed text-white/90">The studio you log into &mdash; web UI, backend APIs, sessions and auth. Everything below plugs in here.</p>
  </div>

  <div class="grid gap-4 md:grid-cols-3">
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Engine</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">Agentic engine</h4>
      <p class="text-sm text-gray-700 leading-relaxed">The proprietary reasoning core.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Module</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">SDTM automation</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Spec-first mapping &amp; sdtm.oak code generation.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Module</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">CRF annotation</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Automated aCRF with confidence-tiered review.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Module</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">USDM</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Protocol &rarr; structured study metadata.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Layer</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">MCP tool layer</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Governed protocol to extend the AI with tools.</p>
    </div>
    <div class="rounded-xl border border-dashed border-gray-300 p-5 flex items-center">
      <p class="text-sm text-gray-500 leading-relaxed">Modules share the same auth, audit and compliance backbone &mdash; one platform, many capabilities.</p>
    </div>
  </div>
</section>

<!-- ======================= LOGICAL ARCHITECTURE ================== -->
<section id="arch-layers" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Logical view</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Five layers, clean seams</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    Before any cloud detail, here is the shape of the system as layers. Each layer is a well-defined seam:
    a request flows from the web studio, through the API, into the engine, out to governed tools, and down to
    storage.
  </p>

  <div class="flex flex-col gap-3">
    <div class="rounded-xl border border-gray-200 border-l-4 border-l-[#1594fd] bg-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8">
      <span class="sm:w-56 flex-shrink-0 text-lg font-semibold text-black">Presentation</span>
      <span class="text-sm text-gray-700 leading-relaxed">Angular web studio &mdash; where you run studies, review outputs, and approve at checkpoints.</span>
    </div>
    <div class="text-center text-gray-400 leading-none">&darr;</div>
    <div class="rounded-xl border border-gray-200 border-l-4 border-l-[#1594fd] bg-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8">
      <span class="sm:w-56 flex-shrink-0 text-lg font-semibold text-black">API &amp; orchestration</span>
      <span class="text-sm text-gray-700 leading-relaxed">FastAPI backend &mdash; authentication, session management, request routing, and job orchestration.</span>
    </div>
    <div class="text-center text-gray-400 leading-none">&darr;</div>
    <div class="rounded-xl bg-[#1594fd] px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8">
      <span class="sm:w-56 flex-shrink-0 text-lg font-semibold text-white">Agentic engine <span class="text-xs font-medium text-white/70">· proprietary</span></span>
      <span class="text-sm text-white/90 leading-relaxed">Reasoning, validation, and human-review checkpoints. Internals are proprietary &mdash; described by inputs and outputs.</span>
    </div>
    <div class="text-center text-gray-400 leading-none">&darr;</div>
    <div class="rounded-xl border border-gray-200 border-l-4 border-l-[#1594fd] bg-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8">
      <span class="sm:w-56 flex-shrink-0 text-lg font-semibold text-black">Tools · MCP</span>
      <span class="text-sm text-gray-700 leading-relaxed">Governed tools &mdash; USDM &amp; BC, CORE validation, filesystem, PDF &amp; R &mdash; reached over MCP.</span>
    </div>
    <div class="text-center text-gray-400 leading-none">&darr;</div>
    <div class="rounded-xl border border-gray-200 border-l-4 border-l-[#1594fd] bg-white px-6 py-5 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-8">
      <span class="sm:w-56 flex-shrink-0 text-lg font-semibold text-black">Data &amp; storage</span>
      <span class="text-sm text-gray-700 leading-relaxed">Managed database, object storage for workspace state &amp; artifacts, and a full audit trail.</span>
    </div>
  </div>
</section>

<!-- =========================== DEPLOYMENT ======================== -->
<section id="arch-deployment" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Reference architecture · AWS</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">How Clinical AI Studio deploys</h1>
  <p class="text-black leading-relaxed font-light mb-6">
    Public traffic reaches Route&nbsp;53, then CloudFront, which terminates TLS and serves the static UI from
    S3. Dynamic calls pass through an Application Load Balancer into container orchestration (ECS or EKS)
    running the backend and per-repository containers. The database is a managed, multi-AZ instance with
    automated backups.
  </p>

  <div class="rounded-xl border-l-4 border-emerald-400 bg-emerald-50 px-5 py-4 mb-10">
    <p class="text-sm text-black leading-relaxed">
      <b>Only port 443 is public</b> &mdash; the single application path is HTTPS via the CDN edge. Every other
      component lives in a private subnet, and all infrastructure access (SSH, database, containers) is through
      <b>OpenVPN only</b>, restricted to allowed users and IPs.
    </p>
  </div>

  <h2 class="text-2xl font-semibold mb-4 text-black">Public edge · AWS-managed</h2>
  <div class="flex flex-col sm:flex-row items-stretch gap-3 mb-10">
    <div class="flex-1 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
      <span class="text-xs font-semibold text-[#1594fd]">DNS</span>
      <p class="text-lg font-semibold text-black">Route 53</p>
    </div>
    <div class="flex items-center justify-center text-gray-400">&rarr;</div>
    <div class="flex-1 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
      <span class="text-xs font-semibold text-[#1594fd]">CDN · TLS 443</span>
      <p class="text-lg font-semibold text-black">CloudFront</p>
    </div>
    <div class="flex items-center justify-center text-gray-400">&rarr;</div>
    <div class="flex-1 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4">
      <span class="text-xs font-semibold text-[#1594fd]">Static UI</span>
      <p class="text-lg font-semibold text-black">S3 (website)</p>
    </div>
  </div>

  <h2 class="text-2xl font-semibold mb-4 text-black">Inside the VPC</h2>
  <div class="rounded-2xl border-2 border-dashed border-[#1594fd] bg-blue-50 p-5">
    <div class="flex items-baseline justify-between gap-4 mb-4">
      <span class="text-sm font-bold uppercase tracking-wider text-[#1594fd]">VPC</span>
      <span class="text-xs font-semibold text-gray-500">All infrastructure access via OpenVPN only</span>
    </div>

    <!-- Public subnet -->
    <div class="rounded-xl border border-gray-200 bg-white p-4 mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <span class="flex-shrink-0 text-xs font-bold uppercase tracking-wider text-[#1594fd]">Public subnet</span>
      <div class="flex-shrink-0 rounded-lg bg-[#1594fd] px-4 py-2 text-white">
        <p class="text-sm font-semibold">OpenVPN gateway</p>
        <p class="text-xs text-white/80">the only public entry point</p>
      </div>
      <span class="text-sm text-gray-700 leading-relaxed">SSH · database · container access all flow through here &mdash; allowed users / IPs only.</span>
    </div>

    <!-- Private subnets -->
    <div class="rounded-xl border border-gray-200 bg-white p-4">
      <span class="text-xs font-bold uppercase tracking-wider text-[#1594fd]">Private subnets · no public route</span>
      <div class="mt-3 flex flex-col md:flex-row gap-4">
        <div class="flex-1 flex flex-col gap-2">
          <div class="rounded-xl bg-gray-50 px-4 py-3">
            <span class="text-xs font-semibold text-[#1594fd]">Load balancer</span>
            <p class="text-lg font-bold text-black">ALB (443)</p>
          </div>
          <div class="text-center text-gray-400 leading-none">&darr;</div>
          <div class="rounded-xl bg-gray-50 px-4 py-3">
            <span class="text-xs font-semibold text-[#1594fd]">Container orchestration · ECS / EKS</span>
            <div class="mt-2 flex flex-wrap gap-2">
              <span class="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold">Backend API</span>
              <span class="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-semibold">Per-repository containers</span>
            </div>
            <p class="mt-2 text-xs text-gray-500 leading-relaxed">One container per repository (user + repo) &mdash; isolated workspace, synced ⇄ S3.</p>
          </div>
        </div>
        <div class="md:w-64 flex flex-col gap-2">
          <div class="rounded-xl bg-gray-50 px-4 py-3">
            <span class="text-xs font-semibold text-[#1594fd]">Workspace &amp; artifact store</span>
            <p class="text-lg font-bold text-black">S3 (durable)</p>
          </div>
          <div class="rounded-xl bg-gray-50 px-4 py-3">
            <span class="text-xs font-semibold text-[#1594fd]">Managed DB · multi-AZ</span>
            <p class="text-lg font-bold text-black">RDS + backups</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ==================== WORKSPACES & ISOLATION =================== -->
<section id="arch-workspaces" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Workspace provisioning &amp; isolation</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Every repository is its own isolated workspace</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    When you create a Cloud Repository, the platform provisions a dedicated container with its own private,
    S3-backed workspace &mdash; one per user and repository, with no shared state across users or repositories.
  </p>

  <div class="grid gap-4 md:grid-cols-4 mb-8">
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-2xl font-extrabold text-[#1594fd]">01</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">Create</h4>
      <p class="text-sm text-gray-700 leading-relaxed">The backend mints a unique <b>repository id</b>, bound to the authenticated user.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-2xl font-extrabold text-[#1594fd]">02</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">Provision</h4>
      <p class="text-sm text-gray-700 leading-relaxed">A Docker orchestrator spins up a dedicated container plus an S3-backed workspace &mdash; one per user &amp; repo.</p>
    </div>
    <div class="rounded-xl border border-gray-200 p-5">
      <span class="text-2xl font-extrabold text-[#1594fd]">03</span>
      <h4 class="text-lg font-semibold text-black mt-1 mb-1">Health-gated</h4>
      <p class="text-sm text-gray-700 leading-relaxed">The studio polls container health until <b>Running</b> before it is usable, and tears it down on delete.</p>
    </div>
    <div class="rounded-xl bg-[#1594fd] p-5 text-white">
      <span class="text-2xl font-extrabold">04</span>
      <h4 class="text-lg font-semibold mt-1 mb-1">Ready</h4>
      <p class="text-sm text-white/90 leading-relaxed">The container exposes REST APIs and its own MCP server at a dedicated per-repo endpoint.</p>
    </div>
  </div>

  <div class="flex flex-col md:flex-row gap-4">
    <div class="flex-1 rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">What the ready workspace exposes</span>
      <div class="mt-4 flex flex-wrap gap-2">
        <span class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium">File CRUD (REST)</span>
        <span class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium">PDF reader</span>
        <span class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium">Filesystem tools</span>
        <span class="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-[#1594fd]">USDM tools (MCP)</span>
        <span class="rounded-full border border-blue-100 bg-blue-50 px-4 py-2 text-sm font-medium text-[#1594fd]">BC tools (MCP)</span>
        <span class="rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium">R console &rarr; workspace terminal</span>
      </div>
    </div>
    <div class="md:w-96 rounded-2xl bg-[#1594fd] p-6 text-white">
      <span class="text-xs font-semibold uppercase tracking-wider text-white/80">Isolation</span>
      <p class="mt-2 text-sm leading-relaxed text-white/90">Each workspace is a separate container with its own private storage &mdash; authenticated per request, with no shared state across users or repositories.</p>
    </div>
  </div>
</section>

<!-- ========================= SECURITY POSTURE =================== -->
<section id="arch-security" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Security posture</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Minimal surface, isolated by design</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    A single public door, everything else locked inside a private network, and every repository isolated from
    the next.
  </p>

  <div class="grid gap-4 md:grid-cols-3">
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Attack surface</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">443-only public</h4>
      <p class="text-sm text-gray-700 leading-relaxed">The only inbound door is HTTPS at the CDN edge. Nothing else is internet-facing.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Network</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">Locked-down VPC</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Everything runs in private subnets; the only public subnet holds the OpenVPN gateway.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Admin access</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">VPN-gated plane</h4>
      <p class="text-sm text-gray-700 leading-relaxed">All infrastructure access &mdash; SSH, DB, containers &mdash; through OpenVPN only, by allowed user and IP.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Isolation</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">Per-repository containers</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Each repository is its own container + workspace &mdash; no cross-repo or cross-user bleed.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Encryption</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">In transit &amp; at rest</h4>
      <p class="text-sm text-gray-700 leading-relaxed">TLS everywhere; encrypted volumes, object storage, and database.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Authorization</span>
      <h4 class="text-lg font-semibold text-black mt-2 mb-1">Least privilege</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Row-level security on user data; components get only the access they need.</p>
    </div>
  </div>
</section>

<!-- ===================== COMPLIANCE & STANDARDS ================= -->
<section id="arch-compliance" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Compliance backbone</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Traceable, reviewable, submission-grade</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    Automation in a regulated domain only counts if it is traceable and reviewable. Four things underpin that.
  </p>

  <div class="grid gap-4 md:grid-cols-2">
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Conformance</span>
      <h4 class="text-xl font-semibold text-black mt-2 mb-2">CDISC CORE</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Outputs are validated against the official CDISC conformance rules engine.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Specifications</span>
      <h4 class="text-xl font-semibold text-black mt-2 mb-2">Sealed mapping specs</h4>
      <p class="text-sm text-gray-700 leading-relaxed">Each mapping spec is human-approved and sealed with a content hash &mdash; tamper-evident from spec to output.</p>
    </div>
    <div class="rounded-2xl border border-gray-200 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Traceability</span>
      <h4 class="text-xl font-semibold text-black mt-2 mb-2">Audit trail + e-sign</h4>
      <p class="text-sm text-gray-700 leading-relaxed">An immutable event log with electronic signatures on every decision.</p>
    </div>
    <div class="rounded-2xl bg-[#1594fd] p-6 text-white">
      <span class="text-xs font-semibold uppercase tracking-wider text-white/80">Oversight</span>
      <h4 class="text-xl font-semibold mt-2 mb-2">Human-in-the-loop</h4>
      <p class="text-sm text-white/90 leading-relaxed">Review checkpoints gate every sensitive step &mdash; nothing auto-commits.</p>
    </div>
  </div>
</section>

<!-- ========================= AGENTIC ENGINE ===================== -->
<section id="arch-engine" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">The engine</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">What goes in, what comes out</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    The engine takes study inputs, reasons over them, validates against the rules, and stops for human review
    at defined checkpoints &mdash; producing submission-ready outputs and the specifications behind them. Its
    orchestration, model routing, and prompt &amp; agent design are proprietary; you work with its inputs and
    outputs.
  </p>

  <div class="flex flex-col lg:flex-row items-stretch gap-4">
    <div class="lg:w-64 flex-shrink-0">
      <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">Inputs</span>
      <div class="mt-3 flex flex-col gap-2">
        <span class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">Protocol</span>
        <span class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">CRF</span>
        <span class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">Statistical analysis plan</span>
        <span class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">Raw data</span>
        <span class="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">CDISC standards</span>
      </div>
    </div>

    <div class="hidden lg:flex items-center text-gray-400 text-2xl">&rarr;</div>

    <div class="flex-1 rounded-2xl bg-[#1594fd] p-6 flex flex-col justify-center gap-4">
      <span class="text-xs font-semibold uppercase tracking-wider text-white/80">Agentic engine</span>
      <div class="flex flex-col gap-3">
        <div class="rounded-xl border border-white/25 bg-white/10 px-5 py-4"><span class="text-base font-bold text-white">Reason &amp; plan</span></div>
        <div class="rounded-xl border border-white/25 bg-white/10 px-5 py-4"><span class="text-base font-bold text-white">Validate against rules</span></div>
        <div class="rounded-xl border border-white/25 bg-white/10 px-5 py-4"><span class="text-base font-bold text-white">Human-review checkpoints</span></div>
      </div>
      <span class="text-xs text-white/75 leading-relaxed">Orchestration · model routing · prompt &amp; agent design &mdash; proprietary.</span>
    </div>

    <div class="hidden lg:flex items-center text-gray-400 text-2xl">&rarr;</div>

    <div class="lg:w-64 flex-shrink-0">
      <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">Outputs</span>
      <div class="mt-3 flex flex-col gap-2">
        <span class="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#1594fd]">SDTM datasets</span>
        <span class="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#1594fd]">ADaM datasets</span>
        <span class="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#1594fd]">Tables, figures, listings</span>
        <span class="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-semibold text-[#1594fd]">Sealed mapping specs</span>
      </div>
    </div>
  </div>
</section>

<!-- ========================== MCP TOOL LAYER ==================== -->
<section id="arch-mcp" class="scroll-mt-16">
  <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Tool enhancement · MCP</p>
  <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Extending the AI with governed tools</h1>
  <p class="text-black leading-relaxed font-light mb-8">
    Deterministic work &mdash; validating conformance, running statistics &mdash; is done by real tools, not
    guessed by the model. MCP (the Model Context Protocol) is the standard, permissioned interface between the
    engine and those tools: every call is explicit and logged. It is the same MCP server that runs inside each
    Cloud Repository workspace.
  </p>

  <div class="flex flex-col lg:flex-row items-center gap-4 mb-8">
    <div class="lg:w-56 flex-shrink-0 rounded-2xl border-2 border-[#1594fd] bg-blue-50 p-6">
      <span class="text-xs font-semibold uppercase tracking-wider text-[#1594fd]">Requests a capability</span>
      <p class="text-xl font-extrabold mt-1 text-black">Agentic engine</p>
    </div>
    <div class="flex flex-col items-center text-gray-400"><span class="text-2xl leading-none">⇄</span><span class="text-xs">MCP</span></div>
    <div class="lg:w-56 flex-shrink-0 rounded-2xl bg-[#1594fd] p-6 text-white">
      <span class="text-xs font-semibold uppercase tracking-wider text-white/80">Governed gateway</span>
      <p class="text-xl font-extrabold mt-1">MCP layer</p>
      <p class="text-xs text-white/90 mt-1 leading-relaxed">Permissioned · logged · standard protocol</p>
    </div>
    <div class="hidden lg:flex items-center text-gray-400 text-2xl">&rarr;</div>
    <div class="flex-1 grid grid-cols-2 gap-3">
      <span class="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold">USDM &amp; BC tools</span>
      <span class="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold">CORE validation</span>
      <span class="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold">Filesystem &amp; PDF</span>
      <span class="rounded-xl border border-gray-200 bg-white px-4 py-4 text-sm font-semibold">Sandboxed R runner</span>
    </div>
  </div>

  <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4">
    <p class="text-sm text-black leading-relaxed">
      The same mechanism lets approved external tools be exposed to the engine under your own governance &mdash;
      extending the AI's reach without loosening control.
    </p>
  </div>
</section>

</div>
